// frontend/src/components/DegreeAudit/SortableProgramList.tsx
//
// Draggable list of degree programs. Allows users to reorder
// majors/minors/gen-ed via drag-and-drop.

import { useState, useCallback, useEffect, useMemo } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
  type DragStartEvent,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical, ChevronDown, ChevronRight } from 'lucide-react';

import RequirementNode, { type RequirementNodeData } from './RequirementNode';
import CreditProgressBar from './Progress/CreditProgressBar';
import GenEdDualProgressBar, { type GenEdProgram } from './Progress/GenEdDualProgressBar';
import RequirementFulfillmentBar, {
  type ProgramWithFulfillment,
} from './Progress/RequirementFulfillmentBar';
import ProgressFormulaPopover from './Progress/ProgressFormulaPopover';
import { isGenEdProgram, isUniversityRequirementsProgram } from '@/utils/programClassification';
import { useCreditUnit } from '../../contexts/InstitutionConfigContext';

export interface ProgramData
  extends Partial<GenEdProgram>,
    Partial<ProgramWithFulfillment> {
  program?: string;
  program_key?: string;
  module?: string;
  track?: string;
  degree_type?: string;
  metadata?: { is_exploratory?: boolean; declaration_deadline_credits?: number; [key: string]: unknown };
  credits_completed?: number;
  credits_in_progress?: number;
  credits_planned?: number;
  credits_required?: number;
  progress?: number;
  requirements?: RequirementNodeData[];
  [key: string]: unknown;
}

export interface MajorGpaInfo {
  current?: number | null;
  projected?: number | null;
}

type ProgramTypeLiteral = 'major' | 'minor' | 'citation' | 'advising' | 'gened';

interface SortableProgramCardProps {
  id: string;
  program: ProgramData;
  programType: ProgramTypeLiteral;
  index: number;
  isExpanded: boolean;
  onToggle: () => void;
  expandedNodes: Record<string, boolean>;
  toggleNode: (path: string) => void;
  majorGpa?: MajorGpaInfo;
  concentrationDepartment?: string;
  onConcentrationChange?: (dept: string) => Promise<void> | void;
}

const SortableProgramCard = ({
  id,
  program,
  programType,
  isExpanded,
  onToggle,
  expandedNodes,
  toggleNode,
  majorGpa,
  concentrationDepartment,
  onConcentrationChange,
}: SortableProgramCardProps) => {
  const creditUnit = useCreditUnit();
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const isGenEd = isGenEdProgram(program);
  const isUnivReq = isUniversityRequirementsProgram(program);
  const isExploratory =
    program.degree_type === 'Exploratory' || !!program.metadata?.is_exploratory;

  const actualType = isGenEd || programType === 'gened'
    ? 'general_education'
    : isUnivReq
      ? 'university_requirements'
      : isExploratory
        ? 'exploratory'
        : programType;

  // UI Phase 2.2c: program-role identity moves from tinted card backgrounds to
  // a small colored dot (same hues as the course-card role stripes); headers
  // share one calm warm surface.
  const ROLE_DOTS: Record<string, string> = {
    major: 'bg-role-major',
    minor: 'bg-role-minor',
    citation: 'bg-role-citation',
    general_education: 'bg-role-gened',
    university_requirements: 'bg-role-university',
    exploratory: 'bg-role-major',
    advising: 'bg-role-advising',
  };
  // Tier 3.5b: role-colored left stripe, mirroring the course-card stripes.
  const ROLE_STRIPES: Record<string, string> = {
    major: 'border-l-role-major',
    minor: 'border-l-role-minor',
    citation: 'border-l-role-citation',
    general_education: 'border-l-role-gened',
    university_requirements: 'border-l-role-university',
    exploratory: 'border-l-role-major',
    advising: 'border-l-role-advising',
  };
  const roleDotClass = ROLE_DOTS[actualType] || 'bg-gray-400';
  const roleStripeClass = ROLE_STRIPES[actualType] || 'border-l-gray-400';

  const declarationDeadline = program.metadata?.declaration_deadline_credits;

  const displayName =
    program.module ||
    (program.program?.includes(program.track ?? '')
      ? program.program
      : `${program.program} - ${program.track}`);

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`border border-line-soft border-l-4 ${roleStripeClass} rounded-card bg-surface-card shadow-sm overflow-hidden`}
    >
      <div
        className={`p-3 md:p-4 bg-surface-sunken/50 ${
          isDragging ? 'cursor-grabbing' : ''
        }`}
      >
        <div className="flex items-center">
          {/* UX-R1 2.1: auto-managed programs (gened) aren't reorderable. */}
          {programType !== 'gened' && (
            <div
              {...attributes}
              {...listeners}
              className="mr-2 p-1 cursor-grab hover:bg-black/5 rounded touch-none"
              title="Drag to reorder"
            >
              <GripVertical className="w-5 h-5 text-ink-3" />
            </div>
          )}

          <div
            className="flex-1 cursor-pointer hover:opacity-80 transition-opacity"
            onClick={onToggle}
          >
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <span
                    className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${roleDotClass}`}
                    aria-hidden="true"
                  />
                  <h3 className="font-semibold text-base md:text-lg break-words text-ink">
                    {displayName}
                  </h3>
                </div>

                <div className="mt-2">
                  {isExploratory && declarationDeadline ? (
                    (() => {
                      const completed = program.credits_completed || 0;
                      const inProgress = program.credits_in_progress || 0;
                      const totalEarned = completed + inProgress;
                      const pct = Math.min(100, (totalEarned / declarationDeadline) * 100);
                      const barColor =
                        pct < 75 ? 'bg-green-500' : pct < 90 ? 'bg-yellow-500' : 'bg-red-500';
                      const textColor =
                        pct < 75
                          ? 'text-green-700'
                          : pct < 90
                            ? 'text-yellow-700'
                            : 'text-red-700';
                      return (
                        <>
                          <div className="w-full bg-surface-sunken rounded-full h-2">
                            <div
                              className={`${barColor} h-2 rounded-full transition-all`}
                              style={{ width: `${pct}%` }}
                            />
                          </div>
                          <div className={`text-xs mt-1 ${textColor}`}>
                            Major declaration milestone: {totalEarned}/{declarationDeadline}{' '}
                            {creditUnit.plural}
                            {pct >= 90
                              ? ' — declare a major soon'
                              : pct >= 75
                                ? ' — start thinking about your major'
                                : ''}
                          </div>
                        </>
                      );
                    })()
                  ) : isGenEd && program.area_fulfillment ? (
                    <GenEdDualProgressBar program={program as GenEdProgram} />
                  ) : program.area_fulfillment ? (
                    <RequirementFulfillmentBar program={program as ProgramWithFulfillment} />
                  ) : (
                    <>
                      <CreditProgressBar
                        majorData={program}
                        totalCredits={
                          program.credits_required || (programType === 'minor' ? 15 : 120)
                        }
                      />
                      <div className="text-xs mt-1 flex items-center gap-1">
                        <ProgressFormulaPopover
                          creditsCompleted={program.credits_completed || 0}
                          creditsInProgress={program.credits_in_progress || 0}
                          creditsPlanned={program.credits_planned || 0}
                          creditsRequired={
                            program.credits_required ||
                            (programType === 'minor' ? 15 : 120)
                          }
                          progress={program.progress || 0}
                          label={
                            actualType === 'minor'
                              ? 'Minor'
                              : actualType === 'general_education'
                                ? 'Gen Ed'
                                : actualType === 'citation'
                                  ? 'Citation'
                                  : actualType === 'advising'
                                    ? 'Advising'
                                    : actualType === 'university_requirements'
                                      ? 'University'
                                      : 'Major'
                          }
                          colorClass={'text-ink-2'}
                        />
                        <span className={'text-ink-2'}>•</span>
                        <span className={'text-ink-2'}>
                          {program.credits_required || (programType === 'minor' ? 15 : 120)}{' '}
                          {creditUnit.plural} required
                        </span>
                      </div>
                    </>
                  )}
                  {majorGpa?.current != null && (
                    <div
                      className="text-xs mt-0.5"
                      title="GPA across courses in this major — current from completed courses; projected includes planned courses."
                    >
                      <span className="font-medium text-primary-strong">
                        Major GPA {majorGpa.current.toFixed(2)}
                      </span>
                      {majorGpa.projected != null &&
                        majorGpa.projected !== majorGpa.current && (
                          <span className="text-ink-3 ml-1">
                            ({majorGpa.projected.toFixed(2)} projected)
                          </span>
                        )}
                    </div>
                  )}
                </div>
              </div>
              <div className="ml-4">
                {isExpanded ? (
                  <ChevronDown className="w-5 h-5 text-gray-500" />
                ) : (
                  <ChevronRight className="w-5 h-5 text-gray-500" />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {isExpanded && program.requirements && (
        <div className="p-3 md:p-4 bg-white border-t border-gray-200 space-y-2">
          {actualType === 'advising' && (
            <div className="flex items-start gap-2 rounded-md border border-status-warning/30 bg-status-warning/10 px-3 py-2 text-xs text-status-warning-fg">
              <span aria-hidden className="mt-0.5">⚠️</span>
              <span>
                This is a <strong>general guideline</strong>, not a graduation requirement — it earns
                no degree credit. Health-professional program prerequisites change often and vary by
                school; always confirm with your pre-health advising office.
              </span>
            </div>
          )}
          {program.requirements.map((requirement, reqIndex) => (
            <RequirementNode
              key={`${id}-req-${reqIndex}`}
              requirement={requirement}
              path={`${id}-req-${reqIndex}`}
              expandedNodes={expandedNodes}
              toggleNode={toggleNode}
              majorData={program}
              programType={actualType}
              programName={displayName ?? null}
              isTopLevel={true}
              concentrationDepartment={concentrationDepartment}
              onConcentrationChange={onConcentrationChange}
            />
          ))}
        </div>
      )}
    </div>
  );
};

interface DragOverlayCardProps {
  program: ProgramData | null;
  programType: ProgramTypeLiteral;
}

const DragOverlayCard = ({ program, programType }: DragOverlayCardProps) => {
  const isGenEd = isGenEdProgram(program);

  const actualType = isGenEd ? 'general_education' : programType;

  const dotClass: Record<string, string> = {
    major: 'bg-role-major',
    minor: 'bg-role-minor',
    general_education: 'bg-role-gened',
  };

  const displayName =
    program?.module ||
    (program?.program?.includes(program?.track ?? '')
      ? program?.program
      : `${program?.program} - ${program?.track}`);

  return (
    <div className="p-3 md:p-4 border-2 border-line-soft rounded-card bg-surface-card shadow-lg">
      <div className="flex items-center gap-2">
        <GripVertical className="w-5 h-5 text-ink-3" />
        <span
          className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${dotClass[actualType] || 'bg-gray-400'}`}
          aria-hidden="true"
        />
        <h3 className="font-semibold text-base text-ink">{displayName}</h3>
      </div>
    </div>
  );
};

const getProgramId = (program: ProgramData, programType: string): string => {
  const name = (program.program || '').replace(/\s+/g, '_');
  const track = (program.track || 'General').replace(/\s+/g, '_');
  return `${programType}-${name}-${track}`;
};

export interface SortableProgramListProps {
  programs: ProgramData[];
  programType: ProgramTypeLiteral;
  expandedNodes: Record<string, boolean>;
  toggleNode: (path: string) => void;
  toggleProgramWithChildren: (
    expansionPath: string,
    requirements: RequirementNodeData[] | undefined
  ) => void;
  onReorder: (newPrograms: ProgramData[]) => Promise<void> | void;
  majorGpas?: Record<string, MajorGpaInfo>;
  concentrationDepartments?: Record<string, string>;
  onConcentrationChange?: (programName: string, dept: string) => Promise<void> | void;
}

const SortableProgramList = ({
  programs,
  programType,
  expandedNodes,
  toggleNode,
  toggleProgramWithChildren,
  onReorder,
  majorGpas,
  concentrationDepartments,
  onConcentrationChange,
}: SortableProgramListProps) => {
  const [activeId, setActiveId] = useState<string | null>(null);
  const [localPrograms, setLocalPrograms] = useState<ProgramData[]>(programs);

  useEffect(() => {
    setLocalPrograms(programs);
  }, [programs]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 8 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const items = localPrograms.map((program) => getProgramId(program, programType));

  const idToIndex = useMemo(() => {
    const map: Record<string, number> = {};
    localPrograms.forEach((program, index) => {
      map[getProgramId(program, programType)] = index;
    });
    return map;
  }, [localPrograms, programType]);

  const handleDragStart = useCallback((event: DragStartEvent) => {
    setActiveId(String(event.active.id));
  }, []);

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      setActiveId(null);

      if (!over || active.id === over.id) {
        return;
      }

      setLocalPrograms((currentPrograms) => {
        const currentIdToIndex: Record<string, number> = {};
        currentPrograms.forEach((program, index) => {
          currentIdToIndex[getProgramId(program, programType)] = index;
        });

        const oldIndex = currentIdToIndex[String(active.id)];
        const newIndex = currentIdToIndex[String(over.id)];

        if (oldIndex === undefined || newIndex === undefined) {
          return currentPrograms;
        }

        const newPrograms = arrayMove(currentPrograms, oldIndex, newIndex);
        onReorder(newPrograms);
        return newPrograms;
      });
    },
    [onReorder, programType]
  );

  const handleDragCancel = useCallback(() => {
    setActiveId(null);
  }, []);

  const activeProgram =
    activeId && idToIndex[activeId] !== undefined
      ? localPrograms[idToIndex[activeId]]
      : null;

  if (!localPrograms || localPrograms.length === 0) {
    return null;
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
    >
      <SortableContext items={items} strategy={verticalListSortingStrategy}>
        <div className="space-y-3">
          {localPrograms.map((program, index) => {
            const id = getProgramId(program, programType);
            const expansionPath = `${programType}-${index}`;
            const isExpanded = expandedNodes[expansionPath];

            return (
              <SortableProgramCard
                key={id}
                id={id}
                program={program}
                programType={programType}
                index={index}
                isExpanded={isExpanded}
                onToggle={() => toggleProgramWithChildren(expansionPath, program.requirements)}
                expandedNodes={expandedNodes}
                toggleNode={toggleNode}
                majorGpa={
                  programType === 'major' && majorGpas
                    ? majorGpas[program.program ?? '']
                    : undefined
                }
                concentrationDepartment={concentrationDepartments?.[program.program ?? '']}
                onConcentrationChange={
                  onConcentrationChange
                    ? (dept) => onConcentrationChange(program.program ?? '', dept)
                    : undefined
                }
              />
            );
          })}
        </div>
      </SortableContext>

      <DragOverlay>
        {activeId ? (
          <DragOverlayCard program={activeProgram} programType={programType} />
        ) : null}
      </DragOverlay>
    </DndContext>
  );
};

export default SortableProgramList;
